import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Color } from '../../_kernel/Color'
import { Title } from '../../_kernel/Title'
import { Subtitle } from '../../_kernel/Subtitle'
import { Url } from '../../_kernel/Url'
import { Device } from '../../shared/domain/Device'
import { BannerType, type ALLOWED_BANNER_TYPE } from './BannerType'
import { type ALLOWED_BANNER_SUB_TYPE, BannerSubtype } from './BannerSubtype'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import { type GETBannerInformationResponseType } from '../infrastructure/HTTPMarketingRepository/schemas/banners'

export const ALLOWED_BANNER_SUBTYPES_BY_TYPE = {
  'billboard-banner': ['brand', 'campaign', 'member'],
} as Partial<Record<(typeof ALLOWED_BANNER_TYPE)[number], Partial<typeof ALLOWED_BANNER_SUB_TYPE>>>

const BannerImageValidation = z.object({
  srcSet: z
    .object({
      DEVICE_MOBILE: z.string().optional(),
      DEVICE_TABLET: z.string().optional(),
      DEVICE_DESKTOP: z.string().optional(),
    })
    .partial()
    .optional()
    .default({}),
  alt: z.string().optional(),
})

export const BannerValidation = z.object({
  type: z.instanceof(BannerType, { message: '[Banner] type value is required' }),
  subtype: z.instanceof(BannerSubtype).optional(),
  viewport: z.instanceof(Device).optional(),
  content: z.object({
    title: z
      .instanceof(Title, { message: '[Banner] title value is required' })
      .array()
      .min(1, { message: '[Banner] title value is required' }),
    description: z.instanceof(Subtitle).optional(),
    url: z.instanceof(Url).optional(),
    ctaText: z.string().optional(),
    bgColor: z.instanceof(Color).optional(),
    titleColor: z.instanceof(Color).optional(),
    descriptionColor: z.instanceof(Color).optional(),
    image: BannerImageValidation.optional(),
  }),
})

type BannerDatasetContent = GETBannerInformationResponseType['banners'][number]['content']

export class Banner extends Model {
  static create({ type, subtype, viewport, content }: z.infer<typeof BannerValidation>) {
    BannerValidation.parse({ type, subtype, viewport, content })
    if (subtype) {
      const validSubtypes = ALLOWED_BANNER_SUBTYPES_BY_TYPE[type.value]
      if (!validSubtypes?.includes(subtype.value)) {
        throw DomainError.withCodes(ErrorsCode.INVALID_BANNER_SUBTYPE).withCauses(
          new Error(`Invalid subtype ${subtype.value} for type ${type.value}`)
        )
      }
    }
    return new Banner(type, content, subtype, viewport)
  }

  static fromJSON(json: ReturnType<Banner['toJSON']>) {
    const { content } = json
    return new Banner(
      BannerType.create({ value: json.type }),
      {
        title: content.title.map(value => Title.create({ value })),
        ...(content.description && {
          description: Subtitle.create({ value: content.description }),
        }),
        ...(content.url && { url: Url.fromJSON(content.url) }),
        ...(content.ctaText && { ctaText: content.ctaText }),
        ...(content.bgColor && { bgColor: Color.create(content.bgColor) }),
        ...(content.titleColor && { titleColor: Color.create(content.titleColor) }),
        ...(content.descriptionColor && {
          descriptionColor: Color.create(content.descriptionColor),
        }),
        ...(content.image && { image: content.image }),
      },
      BannerSubtype.createOrUndefined(json.subtype),
      json.viewport ? Device.create({ value: json.viewport }) : null
    )
  }

  static fromApiDataset({ info }: { info: GETBannerInformationResponseType['banners'][number] }) {
    const subtypeValue = 'subtype' in info ? info.subtype : undefined
    const normalizedContent = Banner.#buildContentFromDataset(info.content)

    return Banner.create({
      type: BannerType.create({ value: info.type }),
      ...(subtypeValue && { subtype: BannerSubtype.create({ value: subtypeValue }) }),
      ...(info.viewport && { viewport: Device.create({ value: info.viewport }) }),
      content: normalizedContent,
    })
  }

  static #processTitle(title: string[]) {
    return title
      .filter(text => typeof text === 'string' && text.trim().length > 0)
      .map(text => Title.create({ value: text }))
  }

  static #processDescription(description: string) {
    return Subtitle.create({ value: description })
  }

  static #processCtaText(ctaText: string) {
    return ctaText
  }

  static #buildContentFromDataset(content: BannerDatasetContent) {
    return {
      title: Banner.#processTitle(content.title),
      ...Banner.#buildOptionalDescription(content),
      ...Banner.#buildOptionalUrl(content),
      ...Banner.#buildOptionalCta(content),
      ...Banner.#buildOptionalBgColor(content),
      ...Banner.#buildOptionalTitleColor(content),
      ...Banner.#buildOptionalDescriptionColor(content),
      ...Banner.#buildOptionalImage(content),
    }
  }

  static #buildOptionalDescription(content: BannerDatasetContent) {
    return 'description' in content && content.description
      ? { description: Banner.#processDescription(content.description) }
      : {}
  }

  static #buildOptionalUrl(content: BannerDatasetContent) {
    return 'url' in content && content.url ? { url: Url.fromJSON({ value: content.url }) } : {}
  }

  static #buildOptionalCta(content: BannerDatasetContent) {
    return 'ctaText' in content && content.ctaText
      ? { ctaText: Banner.#processCtaText(content.ctaText) }
      : {}
  }

  static #buildOptionalBgColor(content: BannerDatasetContent) {
    return 'bgColor' in content && content.bgColor
      ? { bgColor: Color.create({ value: content.bgColor }) }
      : {}
  }

  static #buildOptionalTitleColor(content: BannerDatasetContent) {
    return 'titleColor' in content && content.titleColor
      ? { titleColor: Color.create({ value: content.titleColor }) }
      : {}
  }

  static #buildOptionalDescriptionColor(content: BannerDatasetContent) {
    return 'descriptionColor' in content && content.descriptionColor
      ? { descriptionColor: Color.create({ value: content.descriptionColor }) }
      : {}
  }

  static #buildOptionalImage(content: BannerDatasetContent) {
    return 'image' in content && content.image
      ? {
          image: {
            srcSet: {
              DEVICE_MOBILE: content.image.srcSet?.DEVICE_MOBILE,
              DEVICE_TABLET: content.image.srcSet?.DEVICE_TABLET,
              DEVICE_DESKTOP: content.image.srcSet?.DEVICE_DESKTOP,
            },
            alt: content.image.alt,
          },
        }
      : {}
  }

  constructor(
    public readonly _type: BannerType,
    public readonly _content: z.infer<typeof BannerValidation>['content'],
    public readonly _subtype?: BannerSubtype,
    public readonly _viewport?: Device | null
  ) {
    super()
  }

  get type() { return this._type } // prettier-ignore
  get subtype() { return this._subtype } // prettier-ignore
  get viewport() { return this._viewport ?? null } // prettier-ignore
  get content() { return this._content } // prettier-ignore

  toJSON() {
    const { content } = this
    return {
      type: this.type.value,
      ...(this.subtype && { subtype: this.subtype.value }),
      ...(this.viewport && { viewport: this.viewport.value }),
      content: {
        title: content.title.map(title => title.value),
        ...(content.description && { description: content.description.value }),
        ...(content.url && { url: content.url.toJSON() }),
        ...(content.ctaText && { ctaText: content.ctaText }),
        ...(content.bgColor && { bgColor: content.bgColor.toJSON() }),
        ...(content.titleColor && { titleColor: content.titleColor.toJSON() }),
        ...(content.descriptionColor && { descriptionColor: content.descriptionColor.toJSON() }),
        ...(content.image && { image: content.image }),
      },
    }
  }

  #getBanner() {
    const banner = this.content

    if (!banner) {
      return undefined
    }

    return banner
  }

  #hasNonEmptyTitle() {
    return this.content.title?.some(item => item.value.trim().length > 0)
  }

  #isValidAnnouncementBanner() {
    return Boolean(this.#hasNonEmptyTitle())
  }

  #isValidBillboardBanner() {
    return Boolean(this.#hasNonEmptyTitle() && this.content.description)
  }

  #isValidBlockBanner() {
    return Boolean(
      this.#hasNonEmptyTitle() &&
        this.content.description &&
        this.content.ctaText &&
        this.content.url &&
        this.content.image
    )
  }

  #isValidHeroBanner() {
    return Boolean(this.#hasNonEmptyTitle() && this.content.image)
  }

  getAnnouncementBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidAnnouncementBanner()) {
      return undefined
    }

    return {
      title: banner.title.map(item => item.value),
      bgColor: banner.bgColor?.value,
      titleColor: banner.titleColor?.value,
    }
  }

  getBillboardBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidBillboardBanner()) {
      return undefined
    }

    return {
      title: banner.title.map(item => item.value),
      description: banner.description?.value,
      url: banner.url?.value,
      ctaText: banner.ctaText,
      bgColor: banner.bgColor?.value,
      titleColor: banner.titleColor?.value,
      descriptionColor: banner.descriptionColor?.value,
      image: banner.image
        ? {
            srcSet: Banner.#mapImageToArray(banner.image.srcSet),
            alt: banner.image.alt,
          }
        : undefined,
    }
  }

  getBlockBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidBlockBanner() || !banner.image) {
      return undefined
    }

    return {
      title: banner.title.map(item => item.value),
      description: banner.description?.value,
      url: banner.url?.value,
      ctaText: banner.ctaText,
      image: {
        srcSet: Banner.#mapImageToArray(banner.image.srcSet),
        alt: banner.image.alt,
      },
    }
  }

  getHeroBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidHeroBanner()) {
      return undefined
    }

    const imagesByDevice = banner.image
      ? {
          srcSet: {
            mobile: banner.image.srcSet.DEVICE_MOBILE,
            tablet: banner.image.srcSet.DEVICE_TABLET ?? banner.image.srcSet.DEVICE_MOBILE,
            desktop:
              banner.image.srcSet.DEVICE_DESKTOP ??
              banner.image.srcSet.DEVICE_TABLET ??
              banner.image.srcSet.DEVICE_MOBILE,
          },
          alt: banner.image.alt,
        }
      : undefined

    return {
      title: banner.title.map(item => item.value),
      image: imagesByDevice,
    }
  }

  getType() {
    return this.type.value
  }

  getSubtype() {
    return this.subtype?.value
  }

  static #mapImageToArray(
    srcSet: NonNullable<z.infer<typeof BannerValidation>['content']['image']>['srcSet']
  ) {
    const mobile = srcSet.DEVICE_MOBILE ?? ''
    const tablet = srcSet.DEVICE_TABLET ?? mobile
    const desktop = srcSet.DEVICE_DESKTOP ?? tablet ?? mobile

    return [mobile, tablet, desktop]
  }
}
